import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { EyeIcon, RefreshCw, SearchIcon, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';

import { cn } from '@/lib/utils';
import { setTableState } from '@/model/dataTableModel';
import { selectDataTable } from '..';

import { camelToCapitalized } from '@shared';

import { HorizontalScrollContainer } from './HorizontalScrollContainer';
import { TablePaginationControls } from './TablePaginationControls';

import type {
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  Table as ReactTable,
  Row,
  RowPinningState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends unknown, TValue> {
    label?: string;
  }
}

interface RowMaskConfig<TData> {
  fn: (row: TData) => boolean;
  tooltipContent?: React.ReactNode;
}

// Rows whose values are shown but not counted: their cells are struck through,
// except the listed columns (labels, actions, or a cell that explains why).
interface RowStrikeThroughConfig<TData> {
  fn: (row: TData) => boolean;
  exceptColumns: string[];
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  getRowId: (row: TData) => string;
  data: TData[];
  tableId: string;
  globalSearchText?: string;
  globalSearchFilterKey?: string;
  globalSearchFieldKeys?: string[];
  globalSearchPlaceholder?: string;
  globalSearchAriaLabel?: string;
  handleRefresh?: () => void;
  isLoading?: boolean;
  className?: string;
  initialColumnVisibility?: VisibilityState;
  initialColumnPinning?: ColumnPinningState;
  initialSorting?: SortingState;
  variant?: 'default' | 'outlined';
  isRowDeactivated?: (row: TData) => boolean;
  isRowMasked?: RowMaskConfig<TData>;
  neverMaskedColumns?: string[];
  isRowStruckThrough?: RowStrikeThroughConfig<TData>;
  summaryRowId?: string;
  footer?: React.ReactNode;
  primaryButton?: React.ReactNode;
  headerExtras?: React.ReactNode | ((table: ReactTable<TData>) => React.ReactNode);
  tableSkeletonRows?: number;
  handleSearchTextChange?: (text: string) => void;
  defaultSelection?: TData[];
  pageSize?: number;
  hideColumnSelector?: boolean;
  onSelectionChange?: (selectedRows: TData[]) => void;
  useCustomScrollbar?: boolean;
  onRowClick?: (row: TData) => void;
  renderExpandedRow?: (row: TData) => React.ReactNode;
  emptyMessage?: React.ReactNode;
  renderMobileRow?: (row: TData) => React.ReactNode;
  stackToolbarUntilDesktop?: boolean;
  paginationResetKey?: string;
  stickyHeaderOnPageScroll?: boolean;
  stickyHeaderTopClassName?: string;
  stickyToolbarOnPageScroll?: boolean;
  stickyToolbarTopOffset?: string;
  horizontalScrollOnOverflow?: boolean;
  alwaysShowPagination?: boolean;
}

export function DataTable<TData>({
  columns,
  data,
  globalSearchText,
  globalSearchFilterKey,
  tableId,
  getRowId,
  globalSearchFieldKeys,
  globalSearchPlaceholder = 'Search by name or email...',
  globalSearchAriaLabel = 'Search table',
  handleRefresh,
  isLoading = false,
  className,
  initialColumnVisibility = {},
  initialColumnPinning = { left: [], right: [] },
  initialSorting = [],
  variant = 'default',
  isRowDeactivated,
  isRowMasked,
  neverMaskedColumns = [],
  isRowStruckThrough,
  footer,
  summaryRowId,
  primaryButton,
  headerExtras,
  handleSearchTextChange,
  tableSkeletonRows = 10,
  defaultSelection = [],
  pageSize = 25,
  hideColumnSelector = false,
  onSelectionChange,
  useCustomScrollbar = false,
  onRowClick,
  renderExpandedRow,
  emptyMessage,
  renderMobileRow,
  stackToolbarUntilDesktop = false,
  paginationResetKey,
  stickyHeaderOnPageScroll = false,
  stickyHeaderTopClassName = 'top-0',
  stickyToolbarOnPageScroll = false,
  stickyToolbarTopOffset = '0px',
  horizontalScrollOnOverflow = false,
  alwaysShowPagination = false,
}: DataTableProps<TData>) {
  const dispatch = useDispatch();
  const savedState = useSelector(selectDataTable)[tableId];

  const sortingStorageKey = `sorting:${tableId}`;
  const [sorting, setSorting] = useState<SortingState>(() => {
    const defaultSorting = savedState?.sorting || initialSorting;
    try {
      const stored = JSON.parse(localStorage.getItem(sortingStorageKey) || 'null');
      return stored && stored.id ? [stored] : defaultSorting;
    } catch {
      return defaultSorting;
    }
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    savedState?.columnFilters || [],
  );

  const columnVisibilityStorageKey = `columnVisibility:${tableId}`;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const savedVisibility = savedState?.columnVisibility;
    const defaultVisibility =
      savedVisibility && typeof savedVisibility === 'object' && !Array.isArray(savedVisibility)
        ? { ...initialColumnVisibility, ...savedVisibility }
        : initialColumnVisibility;

    try {
      const stored = JSON.parse(localStorage.getItem(columnVisibilityStorageKey) || 'null');
      if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
        return { ...defaultVisibility, ...stored };
      }
      return defaultVisibility;
    } catch {
      return defaultVisibility;
    }
  });
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(initialColumnPinning);

  const [rowPinning, _setRowPinning] = useState<RowPinningState>({
    top: [],
    bottom: summaryRowId ? [summaryRowId] : [],
  });

  const [rowSelection, setRowSelection] = useState(() => {
    const initialSelection: Record<string, boolean> = {};
    defaultSelection.forEach(row => {
      const rowId = getRowId(row);
      initialSelection[rowId] = true;
    });
    return initialSelection;
  });

  const searchFunction = useMemo(
    () => (row: any) => {
      // Early return if no search text
      if (!globalSearchText?.trim()) return true;

      // Normalize search value once
      const searchValue = globalSearchText.toLowerCase().trim();

      const fieldsToSearch =
        globalSearchFieldKeys && globalSearchFieldKeys.length > 0
          ? globalSearchFieldKeys
          : ['clientName', 'email'];

      // Search through fields
      return fieldsToSearch.some(fieldKey => {
        try {
          const rowValue = row
            .getAllCells()
            .some((cell: { column: { id: string } }) => cell.column.id === fieldKey)
            ? row.getValue(fieldKey)
            : row.original?.[fieldKey];
          const value = String(rowValue || '')
            .toLowerCase()
            .trim();

          // Split search terms for multi-word search
          const searchTerms = searchValue.split(/\s+/);
          return searchTerms.every(term => value.includes(term));
        } catch (error) {
          console.warn(`Search error for field ${fieldKey}:`, error);
          return false;
        }
      });
    },
    [globalSearchText, globalSearchFieldKeys],
  );

  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: updaterOrValue => {
      // onSortingChange accepts either a value or a function per TS types,
      // so we check both even though TanStack always passes a function.
      const nextSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

      setSorting(nextSorting);
      localStorage.setItem(sortingStorageKey, JSON.stringify(nextSorting[0]));
    },
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: updaterOrValue => {
      // onColumnVisibilityChange accepts either a value or a function per TS types,
      // so we check both even though TanStack always passes a function.
      const nextVisibility =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibility) : updaterOrValue;

      setColumnVisibility(nextVisibility);
      localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(nextVisibility));
    },
    onColumnPinningChange: setColumnPinning,
    onRowSelectionChange: setRowSelection,
    autoResetPageIndex: false,
    filterFns: {
      fuzzy: searchFunction,
    },
    globalFilterFn: 'fuzzy' as any,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnPinning,
      rowPinning,
      rowSelection,
    },
    initialState: {
      pagination:
        paginationResetKey !== undefined && savedState?.pagination
          ? savedState.pagination
          : { pageSize },
      columnVisibility: initialColumnVisibility,
      columnPinning: initialColumnPinning,
    },
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const previousPaginationResetKey = useRef(paginationResetKey);

  useEffect(() => {
    if (paginationResetKey === undefined) {
      table.resetPageIndex();
      return;
    }

    const { pageIndex, pageSize: activePageSize } = table.getState().pagination;
    const lastPageIndex = Math.max(0, Math.ceil(filteredRowCount / activePageSize) - 1);
    if (pageIndex > lastPageIndex) table.setPageIndex(lastPageIndex);
  }, [filteredRowCount, paginationResetKey, table]);

  useEffect(() => {
    if (paginationResetKey === undefined) return;
    if (previousPaginationResetKey.current !== paginationResetKey) {
      previousPaginationResetKey.current = paginationResetKey;
      table.setPageIndex(0);
    }
  }, [paginationResetKey, table]);

  const paginationState = table.getState().pagination;

  useEffect(() => {
    dispatch(
      setTableState({
        tableId,
        tableState: {
          sorting,
          columnFilters,
          columnVisibility,
          pagination: paginationState,
        },
      }),
    );
  }, [dispatch, tableId, sorting, columnFilters, columnVisibility, paginationState]);

  useEffect(() => {
    table.setGlobalFilter(globalSearchText);
  }, [globalSearchText, table]);

  const totalRows = summaryRowId ? filteredRowCount - 1 : filteredRowCount;
  const currentPage = paginationState.pageIndex + 1;
  const firstRow = totalRows === 0 ? 0 : (currentPage - 1) * paginationState.pageSize + 1;
  const lastRow = Math.min(currentPage * paginationState.pageSize, totalRows);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyToolbarRef = useRef<HTMLDivElement>(null);
  const [stickyToolbarHeight, setStickyToolbarHeight] = useState(0);

  useEffect(() => {
    if (!stickyToolbarOnPageScroll || !stickyToolbarRef.current) return;

    const toolbar = stickyToolbarRef.current;
    const updateToolbarHeight = () => setStickyToolbarHeight(toolbar.offsetHeight);

    updateToolbarHeight();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateToolbarHeight);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [stickyToolbarOnPageScroll]);

  useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection]);

  return (
    <div
      id={tableId}
      className={className}
      style={
        stickyToolbarOnPageScroll
          ? ({
              '--data-table-sticky-toolbar-top': stickyToolbarTopOffset,
              '--data-table-sticky-header-top': `calc(${stickyToolbarTopOffset} + ${stickyToolbarHeight}px)`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div
        ref={stickyToolbarRef}
        data-testid={stickyToolbarOnPageScroll ? `${tableId}__sticky-toolbar` : undefined}
        className={cn(
          stickyToolbarOnPageScroll &&
            'z-30 flow-root bg-white pb-2 lg:sticky lg:top-[var(--data-table-sticky-toolbar-top)]',
        )}
      >
        <TableToolbar
          table={table}
          tableId={tableId}
          globalSearchText={globalSearchText}
          globalSearchFilterKey={globalSearchFilterKey}
          globalSearchPlaceholder={globalSearchPlaceholder}
          globalSearchAriaLabel={globalSearchAriaLabel}
          handleSearchTextChange={handleSearchTextChange}
          primaryButton={primaryButton}
          headerExtras={headerExtras}
          handleRefresh={handleRefresh}
          isLoading={isLoading}
          hideColumnSelector={hideColumnSelector}
          responsiveLayout={!!renderMobileRow}
          stackToolbarUntilDesktop={stackToolbarUntilDesktop}
        />
      </div>

      <div>
        {isLoading ? (
          <TableSkeleton
            columns={Math.min(columns.length, 6)}
            variant={variant}
            rows={tableSkeletonRows ?? data.length}
          />
        ) : (
          <TableContent
            table={table}
            variant={variant}
            tableContainerRef={tableContainerRef}
            isRowDeactivated={isRowDeactivated}
            isRowMasked={isRowMasked}
            neverMaskedColumns={neverMaskedColumns}
            isRowStruckThrough={isRowStruckThrough}
            summaryRowId={summaryRowId}
            useCustomScrollbar={useCustomScrollbar}
            onRowClick={onRowClick}
            renderExpandedRow={renderExpandedRow}
            emptyMessage={emptyMessage}
            renderMobileRow={renderMobileRow}
            tableId={tableId}
            stickyHeaderOnPageScroll={stickyHeaderOnPageScroll}
            stickyHeaderTopClassName={
              stickyToolbarOnPageScroll
                ? 'top-[var(--data-table-sticky-header-top)]'
                : stickyHeaderTopClassName
            }
            horizontalScrollOnOverflow={horizontalScrollOnOverflow}
          />
        )}
      </div>

      {footer && footer}

      <TablePagination
        table={table}
        totalRows={totalRows}
        firstRow={firstRow}
        lastRow={lastRow}
        responsive={!!renderMobileRow}
        alwaysShow={alwaysShowPagination}
      />
    </div>
  );
}

function TableToolbar<TData>({
  table,
  tableId,
  globalSearchText,
  globalSearchFilterKey,
  globalSearchPlaceholder = 'Search by name or email...',
  globalSearchAriaLabel,
  handleSearchTextChange,
  primaryButton,
  headerExtras,
  handleRefresh,
  isLoading,
  hideColumnSelector,
  responsiveLayout,
  stackToolbarUntilDesktop,
}: {
  table: ReactTable<TData>;
  tableId: string;
  globalSearchText?: string;
  globalSearchFilterKey?: string;
  globalSearchPlaceholder?: string;
  globalSearchAriaLabel: string;
  handleSearchTextChange?: (text: string) => void;
  primaryButton?: React.ReactNode;
  headerExtras?: React.ReactNode | ((table: ReactTable<TData>) => React.ReactNode);
  handleRefresh?: () => void;
  isLoading?: boolean;
  hideColumnSelector?: boolean;
  responsiveLayout: boolean;
  stackToolbarUntilDesktop?: boolean;
}) {
  return (
    <div
      className={cn(
        'mb-4 flex items-center gap-2',
        responsiveLayout && 'flex-col items-stretch lg:flex-row lg:items-center',
        !responsiveLayout &&
          stackToolbarUntilDesktop &&
          'flex-col items-stretch xl:flex-row xl:items-center',
      )}
    >
      {/* Search bar */}
      {handleSearchTextChange && (
        <InputGroup
          className={cn(
            'h-10 w-full md:w-96',
            responsiveLayout && 'md:w-full lg:w-96',
            !responsiveLayout && stackToolbarUntilDesktop && 'md:w-full xl:w-96',
          )}
        >
          <InputGroupAddon>
            <SearchIcon className="text-primary-700" />
          </InputGroupAddon>
          <InputGroupInput
            id="search-field"
            data-testid="search-field"
            name="table-search"
            autoComplete="off"
            aria-label={globalSearchAriaLabel}
            value={globalSearchText}
            onChange={e => handleSearchTextChange(e.target.value)}
            placeholder={globalSearchPlaceholder}
          />
          {globalSearchText && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                onClick={() => handleSearchTextChange('')}
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
      )}
      {/* Column selector and refresh button */}
      <div
        className={cn(
          'flex w-full justify-between gap-2',
          responsiveLayout && 'flex-wrap',
          !responsiveLayout && stackToolbarUntilDesktop && 'items-start',
        )}
      >
        {primaryButton && primaryButton}
        <div
          className={cn(
            'ml-auto flex items-center gap-2',
            responsiveLayout && 'w-full flex-wrap justify-end lg:w-auto lg:flex-nowrap',
            !responsiveLayout &&
              stackToolbarUntilDesktop &&
              'min-w-0 flex-1 items-start justify-end',
          )}
        >
          {headerExtras &&
            (!responsiveLayout && stackToolbarUntilDesktop ? (
              <div className="min-w-0 flex-1">
                {typeof headerExtras === 'function' ? headerExtras(table) : headerExtras}
              </div>
            ) : typeof headerExtras === 'function' ? (
              headerExtras(table)
            ) : (
              headerExtras
            ))}
          {!hideColumnSelector && (
            <ColumnSelector
              table={table}
              tableId={tableId}
            />
          )}
          {handleRefresh && (
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ColumnSelector<TData>({ table, tableId }: { table: ReactTable<TData>; tableId: string }) {
  const anyColumnHidden = table
    .getAllColumns()
    .filter(col => col.getCanHide())
    .some(col => !col.getIsVisible());

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger>
        <Button
          variant="outline"
          id={`${tableId}__column-selector-trigger`}
          className={cn(anyColumnHidden && 'bg-primary/10 border-primary')}
          size="icon"
          aria-label="Show/Hide Columns"
        >
          <EyeIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="rounded-xl p-2"
      >
        {table
          .getAllColumns()
          .filter(column => column.getCanHide())
          .map(column => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className=""
                checked={column.getIsVisible()}
                onCheckedChange={value => column.toggleVisibility(!!value)}
                data-column-id={column.id}
              >
                {column.columnDef.meta?.label ?? camelToCapitalized(column.id)}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TableContent<TData>({
  table,
  variant,
  tableContainerRef,
  isRowDeactivated,
  isRowMasked,
  neverMaskedColumns = [],
  isRowStruckThrough,
  summaryRowId,
  useCustomScrollbar = false,
  onRowClick,
  renderExpandedRow,
  emptyMessage,
  renderMobileRow,
  tableId,
  stickyHeaderOnPageScroll,
  stickyHeaderTopClassName,
  horizontalScrollOnOverflow,
}: {
  table: ReactTable<TData>;
  variant: 'default' | 'outlined';
  tableContainerRef: React.RefObject<HTMLDivElement>;
  isRowDeactivated?: (row: TData) => boolean;
  isRowMasked?: RowMaskConfig<TData>;
  neverMaskedColumns: string[];
  isRowStruckThrough?: RowStrikeThroughConfig<TData>;
  summaryRowId?: string;
  useCustomScrollbar?: boolean;
  onRowClick?: (row: TData) => void;
  renderExpandedRow?: (row: TData) => React.ReactNode;
  emptyMessage?: React.ReactNode;
  renderMobileRow?: (row: TData) => React.ReactNode;
  tableId: string;
  stickyHeaderOnPageScroll: boolean;
  stickyHeaderTopClassName: string;
  horizontalScrollOnOverflow: boolean;
}) {
  const stickyHeaderScrollRef = useRef<HTMLDivElement>(null);

  const renderTableHeader = (layoutOnly = false) => (
    <TableHeader
      aria-hidden={layoutOnly || undefined}
      className={cn(
        variant === 'outlined' ? 'border-primary-200 border-b' : 'border-b-2',
        variant === 'outlined' ? 'bg-primary-50' : 'bg-white',
        layoutOnly && 'invisible',
      )}
    >
      {table.getHeaderGroups().map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            const pinningStyles: React.CSSProperties = header.column.getIsPinned()
              ? {
                  position: 'sticky',
                  left:
                    header.column.getIsPinned() === 'left'
                      ? `${header.column.getStart()}px`
                      : undefined,
                  right:
                    header.column.getIsPinned() === 'right'
                      ? `${header.column.getStart()}px`
                      : undefined,
                  zIndex: 3,
                  backgroundColor: '#faf8fd',
                  boxShadow:
                    '2px 0 5px -2px rgba(0,0,0,0.1), inset -4px 1px 0px -2px rgba(0,0,0,0.1)',
                }
              : {};
            const columnSize = header.column.getSize();
            const headerStyles: React.CSSProperties = {
              ...pinningStyles,
              ...(columnSize > 0 ? { width: `${columnSize}px`, minWidth: `${columnSize}px` } : {}),
            };
            return (
              <TableHead
                key={header.id}
                className={cn('px-3', variant === 'outlined' ? 'bg-primary-50' : 'bg-white')}
                style={headerStyles}
                data-column-id={header.column.id}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );

  const tableContent = (
    <div className="inline-block min-w-full align-top">
      <Table className={cn('relative w-full', stickyHeaderOnPageScroll && 'table-fixed')}>
        {renderTableHeader(stickyHeaderOnPageScroll)}
        <TableBody>
          {table.getTopRows().map(row => (
            <PinnedRow
              key={row.id}
              row={row}
              table={table}
            />
          ))}
          {table.getCenterRows().map((row, index, array) => {
            const masked = isRowMasked?.fn(row.original);
            const struckThrough = isRowStruckThrough?.fn(row.original) ?? false;
            const expandedContent = renderExpandedRow?.(row.original);
            const isExpanded =
              expandedContent !== undefined &&
              expandedContent !== null &&
              expandedContent !== false;
            return (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={
                    row.getIsSelected() ? 'selected' : isExpanded ? 'expanded' : undefined
                  }
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn(
                    'group dark:hover:bg-gray-800/50',
                    onRowClick && 'cursor-pointer',
                    isExpanded && 'bg-primary-50',
                    isRowDeactivated?.(row.original) && 'bg-primary-50 opacity-50',
                    summaryRowId !== undefined && index === array.length - 1 && 'border-b-2',
                  )}
                >
                  {row.getVisibleCells().map(cell => {
                    const isAlwaysVisible = neverMaskedColumns.includes(cell.column.id);
                    const pinningStyles: React.CSSProperties = cell.column.getIsPinned()
                      ? {
                          position: 'sticky',
                          left:
                            cell.column.getIsPinned() === 'left'
                              ? `${cell.column.getStart()}px`
                              : undefined,
                          right:
                            cell.column.getIsPinned() === 'right'
                              ? `${cell.column.getStart()}px`
                              : undefined,
                          zIndex: 2,
                          backgroundColor: 'white',
                          boxShadow:
                            '2px 0 5px -2px rgba(0,0,0,0.1), inset -4px 1px 0px -2px rgba(0,0,0,0.1)',
                        }
                      : {};
                    const columnSize = cell.column.getSize();
                    const cellStyles: React.CSSProperties = {
                      ...pinningStyles,
                      ...(columnSize > 0
                        ? { width: `${columnSize}px`, minWidth: `${columnSize}px` }
                        : {}),
                    };
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'group-hover:bg-primary-50 p-3.5 text-gray-700',
                          struckThrough &&
                            !isRowStruckThrough?.exceptColumns.includes(cell.column.id) &&
                            'line-through decoration-gray-500',
                        )}
                        style={cellStyles}
                        data-column-id={cell.column.id}
                      >
                        {masked && !isAlwaysVisible ? (
                          <MaskedCellContent tooltipContent={isRowMasked?.tooltipContent} />
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {isExpanded && (
                  <TableRow data-expanded-row-for={row.id}>
                    <TableCell
                      colSpan={row.getVisibleCells().length}
                      className="bg-gray-50 p-0"
                    >
                      {expandedContent}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
          {table.getRowModel().rows.length === 0 && emptyMessage && (
            <TableRow>
              <TableCell
                colSpan={table.getVisibleLeafColumns().length}
                className="text-muted-foreground h-20 text-center"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
          {table.getBottomRows().map(row => (
            <PinnedRow
              key={row.id}
              row={row}
              table={table}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const mobileContent = renderMobileRow ? (
    <div
      data-testid={`${tableId}__mobile-list`}
      className="space-y-3 lg:hidden"
    >
      {table.getCenterRows().length === 0 && emptyMessage ? (
        <div className="text-muted-foreground rounded-xl border bg-white px-4 py-12 text-center">
          {emptyMessage}
        </div>
      ) : (
        table
          .getCenterRows()
          .map(row => <React.Fragment key={row.id}>{renderMobileRow(row.original)}</React.Fragment>)
      )}
    </div>
  ) : null;

  if (useCustomScrollbar) {
    return (
      <>
        {mobileContent}
        <div
          data-testid={renderMobileRow ? `${tableId}__desktop-table` : undefined}
          className={cn('relative', renderMobileRow && 'hidden lg:block')}
        >
          <HorizontalScrollContainer
            className={`${variant === 'outlined' ? 'border-primary-200 rounded-lg border' : ''}`}
          >
            {tableContent}
          </HorizontalScrollContainer>
        </div>
      </>
    );
  }

  return (
    <>
      {mobileContent}
      <div
        data-testid={renderMobileRow ? `${tableId}__desktop-table` : undefined}
        className={cn('relative grid min-w-0', renderMobileRow && 'hidden lg:grid')}
      >
        {stickyHeaderOnPageScroll && (
          <div
            data-testid={`${tableId}__sticky-header`}
            className={cn('z-20 h-0 overflow-visible bg-white lg:sticky', stickyHeaderTopClassName)}
          >
            <div className="absolute inset-x-0 top-0 bg-white">
              <div
                ref={stickyHeaderScrollRef}
                data-testid={`${tableId}__sticky-header-scroll-container`}
                className={cn(
                  'overflow-hidden rounded-t-lg bg-white',
                  horizontalScrollOnOverflow && '[&_[data-slot=table-container]]:overflow-visible',
                )}
              >
                <Table className="table-fixed">{renderTableHeader()}</Table>
              </div>
            </div>
          </div>
        )}
        <div
          ref={tableContainerRef}
          data-testid={`${tableId}__scroll-container`}
          className={cn(
            stickyHeaderOnPageScroll && !horizontalScrollOnOverflow
              ? 'overflow-visible'
              : 'max-w-full overflow-x-auto',
            horizontalScrollOnOverflow && '[&_[data-slot=table-container]]:overflow-visible',
            variant === 'outlined' && 'border-primary-200 rounded-lg border',
          )}
          onScroll={event => {
            if (horizontalScrollOnOverflow && stickyHeaderScrollRef.current) {
              stickyHeaderScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
            }
          }}
        >
          {tableContent}
        </div>
      </div>
    </>
  );
}

function MaskedCellContent({ tooltipContent }: { tooltipContent?: React.ReactNode }) {
  if (!tooltipContent) {
    return <span>---</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">---</span>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TablePagination<TData>({
  table,
  totalRows,
  firstRow,
  lastRow,
  responsive,
  alwaysShow,
}: {
  table: ReactTable<TData>;
  totalRows: number;
  firstRow: number;
  lastRow: number;
  responsive: boolean;
  alwaysShow: boolean;
}) {
  const pageSize = table.getState().pagination.pageSize;

  return (
    <TablePaginationControls
      totalRows={totalRows}
      firstRow={firstRow}
      lastRow={lastRow}
      pageSize={pageSize}
      canPreviousPage={table.getCanPreviousPage()}
      canNextPage={table.getCanNextPage()}
      onPreviousPage={() => table.previousPage()}
      onNextPage={() => table.nextPage()}
      onPageSizeChange={size => {
        table.setPageSize(size);
        table.setPageIndex(0);
      }}
      responsive={responsive}
      alwaysShow={alwaysShow}
    />
  );
}

function TableSkeleton({
  columns,
  rows = 10,
  variant = 'default',
}: {
  columns: number;
  rows?: number;
  variant?: 'default' | 'outlined';
}) {
  const columnWidths = Array.from({ length: columns }, () =>
    Math.floor(Math.random() * (150 - 75 + 1) + 75),
  );

  return (
    <div className={`${variant === 'outlined' ? 'border-primary-200 rounded-lg border' : ''}`}>
      <Table>
        <TableHeader
          className={`${
            variant === 'outlined' ? 'border-primary-200 bg-primary-50 border-b' : 'border-b-2'
          }`}
        >
          <TableRow>
            {columnWidths.map((width, index) => (
              <TableHead key={index}>
                <Skeleton
                  className="h-4"
                  style={{ width: `${width}px` }}
                />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow
              key={rowIndex}
              className="hover:bg-primary-50"
            >
              {columnWidths.map((width, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton
                    className="h-3"
                    style={{ width: `${width}px` }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PinnedRow({ row, table }: { row: Row<any>; table: ReactTable<any> }) {
  return (
    <TableRow
      className={cn(row.getIsPinned() === 'bottom' && 'border-primary-200 border-t-4')}
      style={{
        position: 'sticky',
        top: row.getIsPinned() === 'top' ? `${row.getPinnedIndex() * 26 + 48}px` : undefined,
        bottom:
          row.getIsPinned() === 'bottom'
            ? `${(table.getBottomRows().length - 1 - row.getPinnedIndex()) * 26}px`
            : undefined,
      }}
    >
      {row.getVisibleCells().map(cell => {
        const columnSize = cell.column.getSize();
        const cellStyles: React.CSSProperties =
          columnSize > 0 ? { width: `${columnSize}px`, minWidth: `${columnSize}px` } : {};
        return (
          <TableCell
            key={cell.id}
            className="px-3.5 py-4"
            style={cellStyles}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
